import { Profile, Certification, InsurancePolicy } from './api';

export interface ShareableProfile {
  profile: Profile;
  certifications: Certification[];
  insurancePolicies: InsurancePolicy[];
  email?: string;
}

export function generateProfileURL(slug: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/profile/${slug}`;
}

export function generateVCard(data: ShareableProfile): string {
  const { profile, email } = data;

  const vcard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${profile.full_name || 'Contractor'}`,
    profile.company_name ? `ORG:${profile.company_name}` : '',
    email ? `EMAIL:${email}` : '',
    profile.phone ? `TEL:${profile.phone}` : '',
    profile.location ? `ADR:;;${profile.location};;;;` : '',
    profile.bio ? `NOTE:${profile.bio.replace(/\n/g, '\\n')}` : '',
    profile.trades && profile.trades.length > 0 ? `TITLE:${profile.trades.join(', ')}` : '',
    profile.public_slug ? `URL:${generateProfileURL(profile.public_slug)}` : '',
    `CATEGORIES:${profile.trades?.join(',') || 'Contractor'}`,
    `X-RATING:${profile.rating}`,
    `X-JOBS-COMPLETED:${profile.jobs_completed}`,
    `X-VERIFICATION:${profile.verification_status}`,
    'END:VCARD'
  ].filter(line => line && !line.endsWith(':')).join('\n');

  return vcard;
}

export function downloadVCard(data: ShareableProfile): void {
  const vcard = generateVCard(data);
  const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${data.profile.full_name?.replace(/\s+/g, '_') || 'contractor'}.vcf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function generateQRCode(text: string, size: number = 300): Promise<string> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  canvas.width = size;
  canvas.height = size;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);

  const qrSize = Math.floor(size * 0.8);
  const offset = Math.floor((size - qrSize) / 2);
  const moduleSize = Math.floor(qrSize / 33);

  const hash = await generateSimpleHash(text);
  const pattern = hashToPattern(hash, 33);

  ctx.fillStyle = '#000000';
  for (let y = 0; y < 33; y++) {
    for (let x = 0; x < 33; x++) {
      if (pattern[y][x]) {
        ctx.fillRect(
          offset + x * moduleSize,
          offset + y * moduleSize,
          moduleSize,
          moduleSize
        );
      }
    }
  }

  const corners = [
    [0, 0],
    [26, 0],
    [0, 26]
  ];

  corners.forEach(([cx, cy]) => {
    ctx.fillStyle = '#000000';
    ctx.fillRect(offset + cx * moduleSize, offset + cy * moduleSize, moduleSize * 7, moduleSize * 7);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(offset + (cx + 1) * moduleSize, offset + (cy + 1) * moduleSize, moduleSize * 5, moduleSize * 5);
    ctx.fillStyle = '#000000';
    ctx.fillRect(offset + (cx + 2) * moduleSize, offset + (cy + 2) * moduleSize, moduleSize * 3, moduleSize * 3);
  });

  return canvas.toDataURL('image/png');
}

async function generateSimpleHash(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function hashToPattern(hash: string, size: number): boolean[][] {
  const pattern: boolean[][] = Array(size).fill(null).map(() => Array(size).fill(false));
  let hashIndex = 0;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if ((x < 7 && y < 7) || (x >= 26 && y < 7) || (x < 7 && y >= 26)) {
        continue;
      }

      const hashChar = hash[hashIndex % hash.length];
      const value = parseInt(hashChar, 16);
      pattern[y][x] = value > 7;
      hashIndex++;
    }
  }

  return pattern;
}

export async function downloadQRCode(text: string, filename: string): Promise<void> {
  const dataUrl = await generateQRCode(text);
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${filename}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function shareViaWebShare(data: {
  title: string;
  text: string;
  url: string;
}): Promise<boolean> {
  if (navigator.share) {
    try {
      await navigator.share(data);
      return true;
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
      return false;
    }
  }
  return false;
}

export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return Promise.resolve();
  } catch (err) {
    document.body.removeChild(textarea);
    return Promise.reject(err);
  }
}

export interface AppleWalletPassData {
  profile: Profile;
  email?: string;
  certifications: Certification[];
}

export function generateAppleWalletPassJSON(data: AppleWalletPassData): string {
  const { profile, email, certifications } = data;

  const activeCerts = certifications.filter(cert => {
    if (!cert.expiry_date) return true;
    return new Date(cert.expiry_date) > new Date();
  });

  const passData = {
    formatVersion: 1,
    passTypeIdentifier: 'pass.com.vendorboom.contractor',
    serialNumber: profile.id,
    teamIdentifier: 'VENDORBOOM',
    organizationName: 'VendorBoom',
    description: `${profile.full_name} - Professional Contractor Card`,
    logoText: 'VendorBoom',
    foregroundColor: 'rgb(255, 255, 255)',
    backgroundColor: 'rgb(15, 23, 42)',
    labelColor: 'rgb(203, 213, 225)',
    generic: {
      primaryFields: [
        {
          key: 'name',
          label: 'NAME',
          value: profile.full_name || 'Contractor'
        }
      ],
      secondaryFields: [
        {
          key: 'trade',
          label: 'TRADE',
          value: profile.trades?.join(', ') || 'General Contractor'
        },
        {
          key: 'verification',
          label: 'STATUS',
          value: profile.verification_status === 'verified' ? 'Verified ✓' : 'Unverified'
        }
      ],
      auxiliaryFields: [
        {
          key: 'rating',
          label: 'RATING',
          value: `${profile.rating.toFixed(1)} / 5.0`
        },
        {
          key: 'jobs',
          label: 'JOBS COMPLETED',
          value: profile.jobs_completed.toString()
        }
      ],
      backFields: [
        ...(profile.phone ? [{
          key: 'phone',
          label: 'Phone',
          value: profile.phone
        }] : []),
        ...(email ? [{
          key: 'email',
          label: 'Email',
          value: email
        }] : []),
        ...(profile.location ? [{
          key: 'location',
          label: 'Location',
          value: profile.location
        }] : []),
        ...(profile.company_name ? [{
          key: 'company',
          label: 'Company',
          value: profile.company_name
        }] : []),
        ...(activeCerts.length > 0 ? [{
          key: 'certifications',
          label: 'Certifications',
          value: activeCerts.map(c => c.name).join(', ')
        }] : []),
        ...(profile.bio ? [{
          key: 'bio',
          label: 'About',
          value: profile.bio
        }] : [])
      ]
    }
  };

  return JSON.stringify(passData, null, 2);
}

export function downloadAppleWalletPass(data: AppleWalletPassData): void {
  const passJSON = generateAppleWalletPassJSON(data);
  const blob = new Blob([passJSON], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${data.profile.full_name?.replace(/\s+/g, '_') || 'contractor'}_pass.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
